"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useManager = void 0;
const tslib_1 = require("tslib");
const ImpactProvider_1 = require("./ImpactProvider");
const community_1 = require("./community");
const estimateBlockTime_1 = require("./estimateBlockTime");
const estimateRemainingFundsInDays_1 = require("./estimateRemainingFundsInDays");
const filterEvent_1 = require("./filterEvent");
const contracts_1 = require("./contracts");
const internalUseTransaction_1 = require("./internalUseTransaction");
const toNumber_1 = require("./toNumber");
const react_1 = tslib_1.__importStar(require("react"));
const useManager = (communityAddress) => {
    const { signer, address, provider, subgraph, networkId } = react_1.default.useContext(ImpactProvider_1.ImpactProviderContext);
    const [isReady, setIsReady] = (0, react_1.useState)(false);
    const [nextRequestFundsAvailability, setNextRequestFundsAvailability] = (0, react_1.useState)(new Date(0));
    const [fundsRemainingDays, setFundsRemainingDays] = (0, react_1.useState)(0);
    const [canRequestFunds, setCanRequestFunds] = (0, react_1.useState)(false);
    const [community, setCommunity] = (0, react_1.useState)({
        hasFunds: false,
        maxBeneficiaries: 0
    });
    const executeTransaction = (0, internalUseTransaction_1.internalUseTransaction)();
    /**
     * Add beneficiary to community
     * @param {string} beneficiaryAddress Beneficiary address to be added
     * @returns {Promise<TransactionReceipt>} transaction response object
     * @throws {Error} "No signer"
     * @throws {Error} "Community: NOT_MANAGER"
     * @example
     * ```typescript
     * const { addBeneficiary } = useManager('community-address');
     * const tx = await addBeneficiary('beneficiary-address');
     * ```
     */
    const addBeneficiary = async (beneficiaryAddress) => {
        if (!signer || !address) {
            throw new Error('No signer');
        }
        const tx = await (0, community_1.communityContract)(communityAddress).populateTransaction.addBeneficiary(beneficiaryAddress);
        const response = await executeTransaction(tx);
        return response;
    };
    /**
     * Remove beneficiary from community
     * @param {string} beneficiaryAddress Beneficiary address to be removed
     * @returns {Promise<TransactionReceipt>} transaction response object
     * @throws {Error} "No signer"
     * @throws {Error} "Community: NOT_MANAGER"
     * @throws {Error} "Community::removeBeneficiary: NOT_YET"
     * @example
     * ```typescript
     * const { removeBeneficiary } = useManager('community-address');
     * const tx = await removeBeneficiary('beneficiary-address');
     * ```
     */
    const removeBeneficiary = async (beneficiaryAddress) => {
        if (!signer || !address) {
            throw new Error('No signer');
        }
        const tx = await (0, community_1.communityContract)(communityAddress).populateTransaction.removeBeneficiary(beneficiaryAddress);
        const response = await executeTransaction(tx);
        return response;
    };
    /**
     * Lock beneficiary in community
     * @param {string} beneficiaryAddress Beneficiary address to be locked
     * @returns {Promise<TransactionReceipt>} transaction response object
     * @throws {Error} "No signer"
     * @throws {Error} "Community: NOT_MANAGER"
     * @throws {Error} "Community::lockBeneficiary: NOT_YET"
     * @example
     * ```typescript
     * const { lockBeneficiary } = useManager('community-address');
     * const tx = await lockBeneficiary('beneficiary-address');
     * ```
     */
    const lockBeneficiary = async (beneficiaryAddress) => {
        if (!signer || !address) {
            throw new Error('No signer');
        }
        const tx = await (0, community_1.communityContract)(communityAddress).populateTransaction.lockBeneficiary(beneficiaryAddress);
        const response = await executeTransaction(tx);
        return response;
    };
    /**
     * Unlock beneficiary in community
     * @param {string} beneficiaryAddress Beneficiary address to be unlocked
     * @returns {Promise<TransactionReceipt>} transaction response object
     * @throws {Error} "No signer"
     * @throws {Error} "Community: NOT_MANAGER"
     * @throws {Error} "Community::unlockBeneficiary: NOT_YET"
     * @example
     * ```typescript
     * const { unlockBeneficiary } = useManager('community-address');
     * const tx = await unlockBeneficiary('beneficiary-address');
     * ```
     */
    const unlockBeneficiary = async (beneficiaryAddress) => {
        if (!signer || !address) {
            throw new Error('No signer');
        }
        const tx = await (0, community_1.communityContract)(communityAddress).populateTransaction.unlockBeneficiary(beneficiaryAddress);
        const response = await executeTransaction(tx);
        return response;
    };
    /**
     * Allow manager to request funds from the DAO treasury
     * @returns {Promise<number>} amount received from the reserve
     * @throws {Error} "No signer"
     * @throws {Error} "CommunityAdmin::fundCommunity: Not enough funds"
     * @throws {Error} "ERC20: transfer amount exceeds balance"
     * @throws {Error} "Community: NOT_MANAGER"
     * @example
     * ```typescript
     * const { requestFunds } = useManager('community-address');
     * const received = await requestFunds();
     * ```
     */
    const requestFunds = async () => {
        if (!signer || !address) {
            throw new Error('No signer');
        }
        const tx = await (0, community_1.communityContract)(communityAddress).populateTransaction.requestFunds();
        const response = await executeTransaction(tx);
        const received = (0, filterEvent_1.filterEvent)('event CommunityFunded(address indexed community, uint256 amount)', 'CommunityFunded', response);
        return parseInt(received.args[1].toString(), 10);
    };
    /**
     * Verify if an array of addresses can be beneficiary to the community
     * @param {string[]} addresses Array of user addresses to be beneficiaries
     * @returns {Promise<boolean>} true if all are allowed
     * @throws {Error} "UserNotAllowed: <addresses not allowed>"
     * @example
     * ```typescript
     * const { canUsersBeBeneficiaries } = useManager('community-address');
     * const canBe = await canUsersBeBeneficiaries(['address']);
     * ```
     */
    const canUsersBeBeneficiaries = async (addresses) => {
        const beneficiaries = await subgraph.findBeneficiaries(addresses, '{ id, state, community { id } }');
        const notAllowed = beneficiaries.filter(b => { var _a; return b.state === 0 || b.state === 2 || (b.state === 1 && ((_a = b.community) === null || _a === void 0 ? void 0 : _a.id) === communityAddress.toLowerCase()); });
        if (notAllowed.length > 0) {
            throw new Error(`UserNotAllowed: ${notAllowed.map(b => b.id)}`);
        }
        return true;
    };
    (0, react_1.useEffect)(() => {
        // load request funds interval
        const loadRequestFundsTimeInterval = async () => {
            if (!signer || !address) {
                return;
            }
            const { cusd, communityAdmin } = (0, contracts_1.getContracts)(provider, networkId);
            const _communityContract = (0, community_1.communityContract)(communityAddress, provider);
            const [_currentBlockNumber, baseInterval, lastFundRequest, communityBalance, communityGraph] = await Promise.all([
                provider.getBlockNumber(),
                _communityContract.baseInterval(),
                _communityContract.lastFundRequest(),
                cusd.balanceOf(communityAddress),
                subgraph.getCommunityData(communityAddress, '{ baseInterval, claimAmount, beneficiaries, maxBeneficiaries }')
            ]);
            if (parseInt(lastFundRequest, 10) === 0) {
                const ifTrancheNotZero = parseFloat((await communityAdmin.calculateCommunityTrancheAmount(communityAddress)).toString()) > 0;
                setCanRequestFunds(ifTrancheNotZero);
            }
            else {
                const nextRequestFundsBlock = parseInt(lastFundRequest, 10) + parseInt(baseInterval, 10);
                const ifTrancheNotZero = parseFloat((await communityAdmin.calculateCommunityTrancheAmount(communityAddress)).toString()) > 0;
                setNextRequestFundsAvailability((0, estimateBlockTime_1.estimateBlockTime)(_currentBlockNumber, nextRequestFundsBlock));
                setCanRequestFunds(ifTrancheNotZero);
            }
            setFundsRemainingDays((0, estimateRemainingFundsInDays_1.estimateRemainingFundsInDays)({
                baseInterval: communityGraph.baseInterval,
                beneficiaries: communityGraph.beneficiaries,
                claimAmount: parseFloat(communityGraph.claimAmount),
                fundsOnContract: (0, toNumber_1.toNumber)(communityBalance)
            }));
            setCommunity(c => (Object.assign(Object.assign({}, c), { hasFunds: (0, toNumber_1.toNumber)(communityBalance) > parseFloat(communityGraph.claimAmount), maxBeneficiaries: communityGraph.maxBeneficiaries })));
            setIsReady(true);
        };
        loadRequestFundsTimeInterval();
    }, []);
    return {
        addBeneficiary,
        canRequestFunds,
        canUsersBeBeneficiaries,
        community,
        fundsRemainingDays,
        isReady,
        lockBeneficiary,
        nextRequestFundsAvailability,
        removeBeneficiary,
        requestFunds,
        unlockBeneficiary
    };
};
exports.useManager = useManager;
//# sourceMappingURL=useManager.js.map